import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import trLocale from 'date-fns/locale/tr';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getEmptyOffer } from '../../interfaces/Offer/Offer';
import {
  createOffer,
  deleteOffer,
  getOffer,
  getOfferDescription,
  updateOffer,
} from '../../api/Offer';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';
import { OfferType, offerTypeNameMap } from '../../interfaces/Offer/OfferType';
import { getEmptySpendXGetYMetadata } from '../../interfaces/Offer/Metadata/SpendXGetY';
import { SpendXGetYForm } from './OfferForms/SpendXGetYForm';
import { getEmptyFreeShippingMetadata } from '../../interfaces/Offer/Metadata/FreeShipping';
import { FreeShippingForm } from './OfferForms/FreeShippingForm';
import { TranslationAdornment } from '../../components/Translation/TranslationAdornment';
import { BasketDiscountForm } from './OfferForms/BasketDiscountForm';
import { getEmptyBasketDiscountMetadata } from '../../interfaces/Offer/Metadata/BasketDiscount';

const minWidth = 400;
const maxWidth = 600;

export const ModifyOfferPage = () => {
  const navigate = useNavigate();

  const { offerID: offerIDAsStr } = useParams<string>();

  const offerID = offerIDAsStr ? Number(offerIDAsStr) : undefined;
  const isEditing = Number.isFinite(offerID);

  const [offer, setOffer] = useState(getEmptyOffer());
  const [loading, setLoading] = useState(false);
  const [loadingDescription, setLoadingDescription] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);

  const isPastStartAt = offer.startAt < new Date();
  const disableNonFutureFields = isEditing && isPastStartAt;

  const refreshOffer = () => {
    if (!Number.isFinite(offerID)) {
      return;
    }

    setLoading(true);
    getOffer(offerID!)
      .then(setOffer)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isEditing) {
      refreshOffer();
    }
  }, [offerIDAsStr]);

  const getOfferForm = () => {
    switch (offer.type) {
      case OfferType.SPEND_X_GET_Y:
        return (
          offer.spendXGetYMetadata && (
            <SpendXGetYForm
              disabled={disableNonFutureFields}
              value={offer.spendXGetYMetadata}
              onChange={(x) => setOffer({ ...offer, spendXGetYMetadata: x })}
              minWidth={minWidth}
              maxWidth={maxWidth}
            />
          )
        );
      case OfferType.FREE_SHIPPING:
        return (
          offer.freeShippingMetadata && (
            <FreeShippingForm
              disabled={disableNonFutureFields}
              value={offer.freeShippingMetadata}
              onChange={(x) => setOffer({ ...offer, freeShippingMetadata: x })}
              minWidth={minWidth}
              maxWidth={maxWidth}
            />
          )
        );
      case OfferType.BASKET_DISCOUNT:
        return (
          offer.basketDiscountMetadata && (
            <BasketDiscountForm
              disabled={disableNonFutureFields}
              value={offer.basketDiscountMetadata}
              onChange={(x) =>
                setOffer({ ...offer, basketDiscountMetadata: x })
              }
              minWidth={minWidth}
              maxWidth={maxWidth}
            />
          )
        );
      default:
        return null;
    }
  };

  const handleTypeChange = (type: OfferType) => {
    if (type === OfferType.SPEND_X_GET_Y) {
      setOffer({
        ...offer,
        type: OfferType.SPEND_X_GET_Y,
        spendXGetYMetadata: getEmptySpendXGetYMetadata(),
        freeShippingMetadata: undefined,
        basketDiscountMetadata: undefined,
      });
    } else if (type === OfferType.FREE_SHIPPING) {
      setOffer({
        ...offer,
        type: OfferType.FREE_SHIPPING,
        spendXGetYMetadata: undefined,
        freeShippingMetadata: getEmptyFreeShippingMetadata(),
        basketDiscountMetadata: undefined,
      });
    } else if (type === OfferType.BASKET_DISCOUNT) {
      setOffer({
        ...offer,
        type: OfferType.BASKET_DISCOUNT,
        spendXGetYMetadata: undefined,
        freeShippingMetadata: undefined,
        basketDiscountMetadata: getEmptyBasketDiscountMetadata(),
      });
    }
  };

  const handleGetDescription = async () => {
    setLoadingDescription(true);
    getOfferDescription(offer)
      .then((description) => {
        setOffer({ ...offer, description });
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingDescription(false));
  };

  const handleSave = () => {
    if (isEditing) {
      setLoading(true);
      updateOffer(offer)
        .then((o) => {
          toast.success('Kampanya başarıyla güncellendi');
          setOffer(o);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createOffer(offer)
        .then((o) => {
          toast.success('Kampanya başarıyla oluşturuldu');
          navigate(`/sales/offers/${o.id}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const goBack = () => navigate('/sales/offers');

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deleteOffer(offer.id)
      .then(() => {
        toast.success('Kampanya başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili kampanyayı silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}

      <Waiting open={loading} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? 'Kampanyayı Düzenle' : 'Yeni Kampanya'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            disabled={offer.isDeleted}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'fit-content',
          gap: 1.5,
          minWidth: 0,
          alignItems: 'flex-start',
        }}
      >
        {offer.isDeleted && (
          <Alert severity='warning'>
            Bu kampanya silindiği için üzerinde bir değişiklik yapılamaz.
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 3,
              flexGrow: 1,
              minWidth,
              maxWidth,
            }}
          >
            <Typography
              sx={{
                ...sharedStyles.h5,
                fontWeight: 'bold',
              }}
            >
              Genel
            </Typography>
            <Divider />

            <TextField
              label='İsim'
              variant='outlined'
              size='small'
              disabled={offer.isDeleted}
              value={offer.name}
              onChange={(e) => setOffer({ ...offer, name: e.target.value })}
              fullWidth
              multiline
              InputProps={{
                endAdornment: (
                  <TranslationAdornment
                    multiline
                    defaultValue={offer.name}
                    value={offer.nameTranslation}
                    onChange={(x) => setOffer({ ...offer, nameTranslation: x })}
                  />
                ),
              }}
            />

            <TextField
              label='Açıklama'
              variant='outlined'
              size='small'
              disabled={offer.isDeleted}
              value={offer.description}
              onChange={(e) =>
                setOffer({ ...offer, description: e.target.value })
              }
              fullWidth
              multiline
              InputProps={{
                endAdornment: (
                  <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    {loadingDescription ? (
                      <CircularProgress size={24} />
                    ) : (
                      <IconButton
                        size='small'
                        color='primary'
                        onClick={handleGetDescription}
                      >
                        <AutoAwesomeIcon />
                      </IconButton>
                    )}

                    <TranslationAdornment
                      multiline
                      defaultValue={offer.description}
                      value={offer.descriptionTranslation}
                      onChange={(x) =>
                        setOffer({ ...offer, descriptionTranslation: x })
                      }
                    />
                  </Box>
                ),
              }}
            />

            <FormControl
              disabled={disableNonFutureFields}
              size='small'
              fullWidth
            >
              <InputLabel>Kampanya Tipi</InputLabel>
              <Select
                label='Kampanya Tipi'
                value={offer.type}
                onChange={(e) => handleTypeChange(e.target.value as OfferType)}
              >
                {Object.entries(offerTypeNameMap).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={trLocale}
            >
              <DateTimePicker
                label='Başlangıç'
                onChange={(newStartAt) =>
                  setOffer({ ...offer, startAt: newStartAt ?? new Date() })
                }
                disabled={disableNonFutureFields}
                value={offer.startAt}
                inputFormat='d MMMM yyyy HH:mm'
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth />
                )}
              />
            </LocalizationProvider>

            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={trLocale}
            >
              <DateTimePicker
                label='Bitiş'
                disabled={offer.isDeleted}
                onChange={(newEndAt) =>
                  setOffer({ ...offer, endAt: newEndAt ?? new Date() })
                }
                value={offer.endAt}
                inputFormat='d MMMM yyyy HH:mm'
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={offer.endAt < new Date()}
                    size='small'
                    fullWidth
                  />
                )}
              />
            </LocalizationProvider>

            <FormControlLabel
              disabled={offer.isDeleted}
              control={
                <Checkbox
                  checked={offer.isActive}
                  onChange={() =>
                    setOffer({
                      ...offer,
                      isActive: !offer.isActive,
                    })
                  }
                />
              }
              label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
            />
          </Paper>

          {getOfferForm()}
        </Box>

        <Button
          variant='contained'
          disabled={offer.isDeleted}
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText }}
        >
          Kaydet
        </Button>
      </Box>
    </Box>
  );
};
